import React from 'react';
import PageLayout from '../components/PageLayout';
import TechStack from '../components/TechStack';
import SEOHead from '../components/SEOHead';

const expectations = [
  "A solid grasp of your company's requirements",
  'A user-centered strategy',
  'An outstanding product',
  'A reasonable price',
];

const benefits = [
  { icon: '/pictures/agile_development_process_11fe77acf4.svg', title: 'Optimization', desc: 'We tailor solutions to the specific needs of your business, ensuring that it is optimized for your workflow and processes.' },
  { icon: '/pictures/software_f1fba72847.svg', title: 'Better performance', desc: 'It is built using the latest technologies and best practices, ensuring that it is fast, reliable, and secure.' },
  { icon: '/pictures/faster_cycles_cb82a46bc5.svg', title: 'Improved productivity', desc: 'Be more productive by automating tasks and providing your employees with the information they require right at the time they require it.' },
  { icon: '/pictures/support_a1eed40502.svg', title: 'Ongoing support', desc: 'The goal of all we do is to contribute to higher quality and more profitable activity for your business.' },
];

const whyUs = [
  { icon: '/pictures/proven_experience_cfa5dc424e.svg', title: 'Proven experience' },
  { icon: '/pictures/delivering_customized_solutions_081a9bb187.svg', title: 'Bespoke solution' },
  { icon: '/pictures/agile_development_process_11fe77acf4.svg', title: 'End-to-end migration' },
  { icon: '/pictures/focus_on_user_experience_and_design_7b8b7d13c5.svg', title: 'Latest technologies' },
  { icon: '/pictures/effective_framework_9d71acf627.svg', title: 'Effective framework implementation' },
  { icon: '/pictures/global_scale_94d2c82201.svg', title: 'Global scale' },
];

export default function WebAppDev() {
  return (
    <PageLayout>
      <SEOHead
        title="Web Application Development"
        description="Tailored web application development for enterprises and startups. DISION Tech builds responsive, secure, and scalable web apps with React, Node.js and more."
        keywords="web application development, React development, Node.js, frontend development, backend development, full stack, SaaS development"
        url="/development/web-application-development"
      />
      {/* Page Hero */}
      <div style={{ background: '#F9FAFB', padding: '60px 24px 50px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6B7280' }}>Development</span>
            <span style={{ color: '#D1D5DB' }}>/</span>
            <span style={{ fontSize: '14px', color: '#0099CC', fontWeight: '500' }}>Web Application Development</span>
          </div>
          <h1 style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: '800', color: '#111827', marginBottom: '16px', letterSpacing: '-1px', lineHeight: 1.15 }}>
            Web Application Development
          </h1>
          <p style={{ fontSize: '18px', color: '#6B7280', maxWidth: '640px', lineHeight: 1.7 }}>
            Tailored web application development for not only enterprises but also startups and successful tech businesses. We partner with entrepreneurs and businesses on every stage of your digital product development.
          </p>
        </div>
      </div>

      {/* What to expect */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '24px', letterSpacing: '-0.5px' }}>What you can expect from our web application development</h2>
            <p style={{ fontSize: '16px', color: '#6B7280', lineHeight: 1.8, marginBottom: '28px' }}>
              You are facing a difficult but rewarding path ahead that awaits you as a business building or establishing web application development. We accompany you from the initial requirement steps to the delivery of results.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {expectations.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#0099CC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <span style={{ fontSize: '16px', color: '#374151', fontWeight: '500' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ borderRadius: '20px', overflow: 'hidden', lineHeight: 0 }}>
            <img src="/pictures/intro_bg2_7e254d05c7.png" alt="Web App Development" style={{ width: '100%', height: 'auto', display: 'block' }} onError={e => { e.target.src = 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=600&q=80'; }} />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section style={{ padding: '80px 24px', background: '#F9FAFB' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '12px', letterSpacing: '-0.5px' }}>What do you get when developing your own web application?</h2>
          <p style={{ fontSize: '17px', color: '#6B7280', marginBottom: '56px', maxWidth: '500px' }}>Key benefits you receive from our web application development services.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '28px' }}>
            {benefits.map((b, i) => (
              <div key={i} className="service-card" style={{ background: 'white', borderRadius: '16px', padding: '32px', border: '1px solid #E5E7EB', display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                <div style={{ width: '52px', height: '52px', flexShrink: 0, borderRadius: '12px', background: '#F0F9FF', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
                  <img src={b.icon} alt={b.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>{b.title}</h3>
                  <p style={{ fontSize: '15px', color: '#6B7280', lineHeight: 1.7, margin: 0 }}>{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '40px', letterSpacing: '-0.5px' }}>Why choose us</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {whyUs.map((item, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: '20px', borderRadius: '12px', border: '1px solid #E5E7EB', textAlign: 'center' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#F0F9FF', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
                    <img src={item.icon} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151', lineHeight: 1.3 }}>{item.title}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ borderRadius: '20px', overflow: 'hidden', lineHeight: 0 }}>
            <img src="/pictures/intro_bg3_0ae072fd62.jpg" alt="Why choose us" style={{ width: '100%', height: 'auto', display: 'block' }} onError={e => { e.target.src = 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&q=80'; }} />
          </div>
        </div>
      </section>

      {/* Tech stack */}
      <section style={{ padding: '0 24px 80px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '32px', fontWeight: '700', color: '#111827', marginBottom: '40px', letterSpacing: '-0.5px' }}>Our tech stack</h2>
          <TechStack />
        </div>
      </section>
    </PageLayout>
  );
}
